import React, { useState, useEffect, useMemo } from 'react';
import MediaQuery from 'react-responsive';
import PropTypes from 'prop-types';
import { get, uniqBy, isEmpty } from 'lodash';

import List from './List';
import FullList from './FullList';
import Line from '../../common/Line';
import Popup from '../../common/Popup';
import Description from './Description';
import PopoverMenu from '../../common/PopoverMenu';
import { BREAKPOINT } from '../../../constants/styles';
import { COMMUNITIES_USER_ROLES } from '../../../constants';
import { useToggleFlag } from '../../../hocs/useToogleFlag';
import SendMessagePopup from '../../common/SendMessagePopup';
import ActionConfirmation from '../../common/ActionConfirmation';
import { ALLOW_SOCIAL_CHAT_CREATION } from '../../../constants/chat';
import {
  COMMUNITY_MEMBERS_LIST,
  COMMUNITY_MODERATORS_LIST,
} from '../../../constants/strings';
import { enhance } from './enhance';

const About = ({
  community,
  intl,
  loggedUserId,
  membersList,
  moderatorsList,
  isModerator,
  sendMessage,
  isPublic,
  isMember,
  communityId,
  moderatorsCount,
  membersCount,
  clearMembers,
  clearModerators,
  fetchMembers,
  fetchModerators,
  getMenuItems,
  removeUserCommunity,
}) => {
  const { formatMessage } = intl;
  const { description } = community;

  useEffect(() => {
    clearMembers();
    clearModerators();

    if (isMember) {
      if (isPublic || isModerator) fetchMembers(communityId);
      if (isModerator) fetchModerators(communityId);
    }

    return () => {
      clearMembers();
      clearModerators();
    };
  }, [isMember]);
  const [confirmOpened, openConfirm, closeConfirm] = useToggleFlag(false);
  const [isOpened, togglePopup] = useState(false);
  const [listType, setListType] = useState(COMMUNITIES_USER_ROLES.MEMBER);
  const [isOpenPopover, togglePopover] = useState(false);
  const [anchorListItemMenuEl, setAnchorListItemMenuEl] = useState(null);
  const [selectedProfile, selectProfile] = useState({});
  const [isOpenSendMessage, setOpenSendMessage] = useState(false);

  const moderatorsListTitle = useMemo(
    () =>
      formatMessage(COMMUNITY_MODERATORS_LIST, {
        count: moderatorsCount,
      }),
    [moderatorsCount],
  );
  const membersTitle = useMemo(
    () =>
      formatMessage(COMMUNITY_MEMBERS_LIST, {
        count: membersCount,
      }),
    [membersCount],
  );
  const sendMessagePopupTitle = useMemo(
    () => formatMessage({ id: 'chat.actions.sendMessage' }),
    [],
  );

  const hasMembers = membersList.length !== 0;
  const hasModerators = moderatorsList.length !== 0;

  const totalMembers = uniqBy(membersList, item => item.id);

  const isListTypeMembers = listType === COMMUNITIES_USER_ROLES.MEMBER;
  const popupTitle = isListTypeMembers ? membersTitle : moderatorsListTitle;
  const popupItems = isListTypeMembers ? totalMembers : moderatorsList;

  const handleListClick = type => {
    togglePopup(true);
    setListType(type);
  };

  const handlePopupClose = () => {
    togglePopup(false);
  };

  const handleSendMessage = () => {
    togglePopover(false);
    setOpenSendMessage(true);
  };

  const handleRemoveUser = () => {
    openConfirm();
    togglePopover(false);
  };

  const handleRemovePopoverAction = () => {
    removeUserCommunity(listType, communityId, selectedProfile.id);
  };

  const handleSubmitSendMessage = body => {
    const message = { body, recipientId: selectedProfile.id };
    sendMessage({ message });
  };

  const handleListItemMenuClick = (e, profile, type) => {
    e.preventDefault();
    e.stopPropagation();

    togglePopover(true);
    selectProfile(profile);
    setListType(type);

    setAnchorListItemMenuEl(e.currentTarget);
  };

  const getSendMessageOption = () => {
    if (ALLOW_SOCIAL_CHAT_CREATION) {
      return { sendMessage: handleSendMessage };
    }
    return {};
  };

  const getMembersListMenuOptions = () => {
    let options = {};
    if (isModerator) {
      options = { ...options, ...{ removeFromCommunity: handleRemoveUser } };
    }
    return { ...options, ...getSendMessageOption() };
  };

  const memberListOptions = getMembersListMenuOptions();

  const menuItems = isListTypeMembers
    ? memberListOptions
    : getSendMessageOption();

  const menu = getMenuItems(menuItems);

  const showMembersListMenu = Boolean(!isEmpty(memberListOptions));

  const popoverMenu = (
    <PopoverMenu
      size="small"
      isOpen={isOpenPopover}
      anchorEl={anchorListItemMenuEl}
      container={get(anchorListItemMenuEl, 'current.parentNode.parentNode')}
      items={menu}
      onClose={() => {
        togglePopover(false);
      }}
    />
  );

  const popup = (
    <MediaQuery maxWidth={BREAKPOINT}>
      {isMobile => (
        <Popup
          onClose={handlePopupClose}
          title={popupTitle}
          opened={isOpened}
          width={isMobile ? 'medium' : 'small'}
          strictClose={isOpenSendMessage || isOpenPopover || confirmOpened}
        >
          <FullList
            items={popupItems}
            isMobile={isMobile}
            loggedUserId={loggedUserId}
            onListItemMenuClick={handleListItemMenuClick}
            listType={listType}
            showMenu={
              isListTypeMembers
                ? showMembersListMenu
                : ALLOW_SOCIAL_CHAT_CREATION
            }
          />
        </Popup>
      )}
    </MediaQuery>
  );

  const handleCloseSendMessage = () => {
    setOpenSendMessage(false);
  };

  return (
    <div styleName="about">
      <Description text={description || 'No description'} />
      <If condition={hasModerators}>
        <Line />
        <List
          items={moderatorsList}
          showMenu={ALLOW_SOCIAL_CHAT_CREATION}
          title={moderatorsListTitle.toUpperCase()}
          loggedUserId={loggedUserId}
          limitList={7}
          onListItemMenuClick={handleListItemMenuClick}
          type={COMMUNITIES_USER_ROLES.MODERATOR}
          onSeeAll={() => {
            handleListClick(COMMUNITIES_USER_ROLES.MODERATOR);
          }}
        />
      </If>
      <If condition={hasMembers}>
        <Line />
        <List
          items={totalMembers}
          showMenu={showMembersListMenu}
          title={membersTitle.toUpperCase()}
          loggedUserId={loggedUserId}
          limitList={7}
          type={COMMUNITIES_USER_ROLES.MEMBER}
          onListItemMenuClick={handleListItemMenuClick}
          onSeeAll={() => {
            handleListClick(COMMUNITIES_USER_ROLES.MEMBER);
          }}
        />
      </If>
      <ActionConfirmation
        intl={intl}
        onClose={closeConfirm}
        onSubmit={handleRemovePopoverAction}
        opened={confirmOpened}
        title={formatMessage({ id: 'community.menu.removeMember' })}
        body={formatMessage({ id: 'community.menu.removeMemberMessage' })}
        actionButtonText={formatMessage({ id: 'common.remove' })}
        width="xsmall"
      />
      {isOpenSendMessage && (
        <SendMessagePopup
          opened
          title={sendMessagePopupTitle}
          onSubmit={handleSubmitSendMessage}
          onClose={handleCloseSendMessage}
        />
      )}
      {isOpenPopover && popoverMenu}
      {isOpened && popup}
    </div>
  );
};

About.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  community: PropTypes.object.isRequired,
  loggedUserId: PropTypes.number.isRequired,
  communityId: PropTypes.number.isRequired,
  membersList: PropTypes.arrayOf(PropTypes.object).isRequired,
  moderatorsList: PropTypes.arrayOf(PropTypes.object).isRequired,
  isModerator: PropTypes.bool.isRequired,
  sendMessage: PropTypes.func.isRequired,
  isMember: PropTypes.bool.isRequired,
  isPublic: PropTypes.bool.isRequired,
  moderatorsCount: PropTypes.number.isRequired,
  membersCount: PropTypes.number.isRequired,
  clearMembers: PropTypes.func.isRequired,
  clearModerators: PropTypes.func.isRequired,
  fetchMembers: PropTypes.func.isRequired,
  fetchModerators: PropTypes.func.isRequired,
  getMenuItems: PropTypes.func.isRequired,
  removeUserCommunity: PropTypes.func.isRequired,
};

export default enhance(About);



// WEBPACK FOOTER //
// ./src/components/Community/About/index.jsx